import React, { ComponentType, useState, useEffect, useCallback, memo } from 'react';
import { FixedSizeGrid as _FixedSizeGrid_, FixedSizeGridProps, GridChildComponentProps } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  Button,
} from '@artstorefronts/ui';
import { ImageIcon, Filter, Check } from 'lucide-react';
import DownloadAllMockupsButton from './DownloadAllMockupsButton';
import CompactDimensionsInput from './CompactDimensionsInput';
import lunr from 'lunr';
import { AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/auth';
import { FETCH_MOCKS, FETCH_MOCK_FILTERS } from '../graphql/queries';
import MockOverlay from './Mock';
import MockPreview from './MockPreview';
import { useSavedContent } from '../context/savedContent';
import { ImageDimensions } from '~/types';
import { filterMockupsByDimensions } from '@arthelper/shared/dimensions';
import { LoadingSpinner } from './MediaSelector/components/LoadingSpinner';

const FixedSizeGrid = _FixedSizeGrid_ as unknown as ComponentType<FixedSizeGridProps>;

interface MocksProps {
  imageId: number;
  trigger?: React.ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
  imageDimensions: ImageDimensions;
  imageOriginalWidth?: number;
  imageOriginalHeight?: number;
  triggerTimestamp?: number;
  setTriggerTimestamp?: (timestamp: number) => void;
}

interface Mock {
  id: string;
  uuid: string;
  description: string;
  tags: [string];
  palette: [string];
  scaleFactor?: number;
  maxWidth?: number;
  maxHeight?: number;
  filters?: MockFilter[];
}

interface MockFilter {
  id: string;
  name: string;
}

interface PreviewMock {
  id: string;
  uuid: string;
  description?: string;
  tags?: [string];
  palette?: [string];
  scaleFactor?: number;
  maxWidth?: number;
  maxHeight?: number;
  filters?: MockFilter[];
}

interface CellData {
  mocks: PreviewMock[];
  imageId: number;
  asfId: string;
  setSelectedMock: (mock: PreviewMock | null) => void;
  columnCount: number;
  mockSaves: any[];
  onSaveChange: (mockId: string, isSaved: boolean) => void;
  imageDimensions: ImageDimensions;
}

const Cell = memo(
  ({ columnIndex, rowIndex, style, data }: GridChildComponentProps<CellData>) => {
    const { mocks, imageId, asfId, setSelectedMock, columnCount, mockSaves, onSaveChange, imageDimensions } = data;
    const index = rowIndex * columnCount + columnIndex;
    const mock = index < mocks.length ? mocks[index] : null;

    if (!mock) return null;

    const mockDimensions = {
      mockScaleFactor: mock.scaleFactor,
    };

    const isSaved = mockSaves.some((save) => save.mockId === mock.id && save.imageId === imageId.toString());

    return (
      <div style={style} className="p-2">
        <MockPreview
          mock={mock}
          asfId={asfId}
          imageId={imageId}
          onSelect={setSelectedMock}
          imageDimensions={imageDimensions}
          mockDimensions={mockDimensions}
          showPalette={true}
          showDownloadButton={true}
          isSaved={isSaved}
          onSaveChange={(isSaved) => onSaveChange(mock.id, isSaved)}
        />
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary re-renders
    const prevData = prevProps.data;
    const nextData = nextProps.data;

    // If the mock at this position hasn't changed and its saved state hasn't changed, don't re-render
    const prevIndex = prevProps.rowIndex * prevData.columnCount + prevProps.columnIndex;
    const nextIndex = nextProps.rowIndex * nextData.columnCount + nextProps.columnIndex;

    const prevMock = prevIndex < prevData.mocks.length ? prevData.mocks[prevIndex] : null;
    const nextMock = nextIndex < nextData.mocks.length ? nextData.mocks[nextIndex] : null;

    if (!prevMock || !nextMock) return prevMock === nextMock;

    const prevIsSaved = prevData.mockSaves.some(
      (save) => save.mockId === prevMock.id && save.imageId === prevData.imageId.toString()
    );
    const nextIsSaved = nextData.mockSaves.some(
      (save) => save.mockId === nextMock.id && save.imageId === nextData.imageId.toString()
    );

    return prevMock.id === nextMock.id && prevIsSaved === nextIsSaved && prevProps.style === nextProps.style;
  }
);

export function Mocks({
  imageId,
  trigger,
  open,
  setOpen,
  imageDimensions,
  triggerTimestamp,
  setTriggerTimestamp,
}: MocksProps) {
  const { t } = useTranslation('artwork');
  const { t: tc } = useTranslation('common');
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [searchIndex, setSearchIndex] = useState<lunr.Index | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<string[] | null>(null);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const { data, loading, refetch } = useQuery(FETCH_MOCKS, {
    variables: selectedFilters ? { filterIds: selectedFilters } : {},
    fetchPolicy: 'no-cache',
  });
  const { data: filtersData } = useQuery(FETCH_MOCK_FILTERS);
  const [selectedMock, setSelectedMock] = useState<PreviewMock | null>(null);
  const [columnCount, setColumnCount] = useState(3);
  const { mockSaves, handleMockSave } = useSavedContent();

  useEffect(() => {
    if (filtersData?.mockFilters) {
      setSelectedFilters(filtersData.mockFilters.map((filter: MockFilter) => filter.id));
    }
  }, [filtersData?.mockFilters]);

  const handleSaveChange = useCallback(
    async (mockId: string, isSaved: boolean) => {
      try {
        await handleMockSave(String(imageId), mockId, isSaved);
      } catch (error) {
        console.error('Error handling mock save:', error);
      }
    },
    [imageId, handleMockSave]
  );

  const setSelectedMockCallback = useCallback((mock: PreviewMock | null) => {
    setSelectedMock(mock);
  }, []);

  const toggleFilter = useCallback((filterId: string) => {
    setSelectedFilters((prev) => {
      if (prev && prev.includes(filterId)) {
        return prev.filter((id) => id !== filterId);
      } else {
        return prev ? [...prev, filterId] : [filterId];
      }
    });
  }, []);

  useEffect(() => {
    if (data?.mocks) {
      const idx = lunr(function () {
        this.field('description');
        this.field('tags');
        this.field('palette');
        data.mocks.forEach((mock: PreviewMock) => {
          this.add({
            id: mock.id,
            description: mock.description || '',
            tags: mock.tags?.join(' ') || '',
            palette: mock.palette?.join(' ') || '',
          });
        });
      });
      setSearchIndex(idx);
    }
  }, [data]);

  useEffect(() => {
    if (!open) {
      setSelectedMock(null);
    }
  }, [open]);

  useEffect(() => {
    const handleResize = () => {
      setColumnCount(window.innerWidth < 1170 ? 2 : 3);
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (triggerTimestamp) {
      refetch();
    }
  }, [triggerTimestamp, refetch]);

  const filteredMocks = React.useMemo(() => {
    if (!data?.mocks) return [];

    const searchFiltered = (() => {
      if (!search || !searchIndex) return data.mocks;

      const results = searchIndex.search(search);
      const resultIds = new Set(results.map((r) => r.ref));
      return data.mocks.filter((mock: PreviewMock) => resultIds.has(mock.id));
    })();

    const { imageWidth, imageHeight, imageDimensionType } = imageDimensions;

    if (imageWidth && imageHeight && imageDimensionType) {
      return filterMockupsByDimensions(searchFiltered, imageWidth, imageHeight, imageDimensionType);
    }

    return searchFiltered;
  }, [data?.mocks, search, searchIndex, imageDimensions]);

  const itemData = React.useMemo(
    () => ({
      mocks: filteredMocks,
      imageId,
      asfId: user?.user_id || '',
      setSelectedMock: setSelectedMockCallback,
      columnCount,
      mockSaves,
      onSaveChange: handleSaveChange,
      imageDimensions,
    }),
    [
      filteredMocks,
      imageId,
      user?.user_id,
      columnCount,
      mockSaves,
      setSelectedMockCallback,
      handleSaveChange,
      imageDimensions,
    ]
  );

  const mockDimensions = React.useMemo(() => {
    return {
      mockScaleFactor: selectedMock?.scaleFactor,
    };
  }, [selectedMock]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent
        className="w-[100vw] h-[100dvh] md:max-w-[60vw] md:w-[60vw] md:h-[95dvh] md:max-h-[95dvh]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <ImageIcon className="w-6 h-6 mr-2" /> {t('mockups.dialog-title')}
          </DialogTitle>
        </DialogHeader>
        <div className="relative">
          <div className="px-2">
            <div className="flex flex-col lg:flex-row lg:items-center lg:gap-4">
              <div className="flex items-center gap-2 mb-4 w-full">
                <div className="flex-1">
                  <Input
                    placeholder={t('mockups.search-placeholder')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-10"
                  />
                </div>

                <div className="w-auto">
                  <DropdownMenu open={isFilterMenuOpen} onOpenChange={setIsFilterMenuOpen}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="h-10 flex items-center gap-1"
                        title={t('mockups.filter-mockups-title')}
                      >
                        <Filter size={16} />
                        <span className="hidden sm:inline">{tc('filters')}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>{t('mockups.apply-filters')}</DropdownMenuLabel>
                      <DropdownMenuSeparator />

                      {filtersData?.mockFilters?.map((filter: MockFilter) => (
                        <DropdownMenuItem
                          key={filter.id}
                          onSelect={(e) => {
                            e.preventDefault();
                            toggleFilter(filter.id);
                          }}
                          className="justify-between"
                        >
                          <span>{filter.name}</span>
                          {selectedFilters && selectedFilters.includes(filter.id) && <Check className="h-4 w-4" />}
                        </DropdownMenuItem>
                      ))}

                      {(!filtersData?.mockFilters || filtersData.mockFilters.length === 0) && (
                        <DropdownMenuItem disabled>
                          <span className="text-muted-foreground">{t('mockups.no-filters-available')}</span>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <div className="w-auto mb-4">
                <DownloadAllMockupsButton
                  key={`download-all-${imageId}-${triggerTimestamp}`}
                  imageId={imageId}
                  open={open}
                  className="h-10"
                />
              </div>
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center lg:gap-4 mb-4">
              <div className="w-auto">
                <CompactDimensionsInput
                  imageId={imageId}
                  imageDimensions={imageDimensions}
                  setTriggerTimestamp={setTriggerTimestamp}
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="h-[calc(95dvh-100px)] md:h-[calc(95dvh-200px)]">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="h-[calc(95dvh-100px)] md:h-[calc(95dvh-200px)]">
              <AutoSizer>
                {({ height, width }) => {
                  const columnWidth = width / columnCount;
                  return (
                    <div className="relative">
                      <FixedSizeGrid
                        columnCount={columnCount}
                        columnWidth={columnWidth}
                        height={height}
                        rowCount={Math.ceil(filteredMocks.length / columnCount)}
                        rowHeight={columnWidth}
                        width={width}
                        itemData={itemData}
                        overscanRowCount={4}
                      >
                        {Cell}
                      </FixedSizeGrid>
                      <AnimatePresence>
                        {selectedMock && (
                          <MockOverlay
                            mock={selectedMock as Mock}
                            imageId={imageId}
                            asfId={user?.user_id || ''}
                            onClose={() => setSelectedMock(null)}
                            imageDimensions={itemData.imageDimensions}
                            mockDimensions={mockDimensions}
                            width={width}
                            height={height}
                          />
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }}
              </AutoSizer>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default Mocks;
